import { useState, useMemo, Children, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Tabs,
  Tab,
  Checkbox,
  Chip,
  IconButton,
} from "@mui/material";
import { ArrowForwardIcon } from "src/Pages/components/Icons/Icons";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import WarningIcon from "@mui/icons-material/Warning";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import { CheckIcon } from "src/Pages/components/Icons/Icons";
import { CardCore } from "src/Pages/components/CardCore/CardCore";
import { AppTextField, ThCoreIndex, IDataTh, TableRowCore, TdCore } from "src/Pages/components";
import { TableCore } from "src/Pages/components/table/BaseTableLoading/BaseTableLoading";
import { FormatDateBR } from "src/shared/Utils/FormatDateBR";
import { ButtonCore } from "src/Pages/components/ButtonCore/ButtonCore";
import { ModalEditarParcela } from "./ModalEditarParcela";
import { IListUploadInstallments } from "src/Contexts/Parcelas/ParcelasTypes";
import { useContextParcelas } from "src/Contexts/Parcelas/ParcelasContext";


interface InstallmentData {
  id: string;
  parcela: number;
  beneficiario: string;
  cpf: string;
  vidas: number;
  vendedor: string;
  codVenda: number;
  vencimento: string;
  valorBoleta: number;
  valorSistema?: number;
  comissao: number;
  status: IListUploadInstallments["data"][0]["status"];
  comissaoCorretora: number;
  comissaoCorretor: number;
  comissaoSupervisor: number;
  comissaoGerente: number;
  lucroParcela: number;
  porcentagemCorretora: number;
  porcentagemCorretor: number;
  porcentagemSupervisor: number;
  porcentagemGerente: number;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value) + "%";
};


const getStatusChip = (status: IListUploadInstallments["data"][0]["status"]) => {
  switch (status) {
    case "OK":
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: "16px" }} />}
          label="Correspondente"
          size="small"
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 500,
            height: "24px",
          }}
        />
      );
    case "NEW_INSTALLMENT":
      return (
        <Chip
          icon={<AddCircleIcon sx={{ fontSize: "16px" }} />}
          label="+ Nova"
          size="small"
          sx={{
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            fontWeight: 500,
            height: "24px",
          }}
        />
      );
    case "VALUE_DIVERGENCE":
      return (
        <Chip
          icon={<WarningIcon sx={{ fontSize: "16px" }} />}
          label="▲ Divergente"
          size="small"
          sx={{
            backgroundColor: "#fff3e0",
            color: "#f57c00",
            fontWeight: 500,
            height: "24px",
          }}
        />
      );
    case "CONTRACT_NOT_FOUND":
      return (
        <Chip
          icon={<CancelIcon sx={{ fontSize: "16px" }} />}
          label="Não encontrada"
          size="small"
          sx={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            fontWeight: 500,
            height: "24px",
          }}
        />
      );
    case "IS_RECEIVED":
      return (
        <Chip
          icon={<CheckIcon color="success" sx={{ fontSize: "16px" }} />}
          label="Recebida"
          size="small"
          sx={{
            backgroundColor: "#EBF9EF",
            color: "#069A35FF",
            fontWeight: 500,
            height: "24px",
          }}
        />
      );
    default:
      return null;
  }
};

const THeadInstallments = () => {
  const dataTh: IDataTh[] = [
    {
      title: "",
      width: "50px",
      align: "center",
    },
    {
      title: "Status",
      align: "left",
      minWidth: "120px",
    },
    {
      title: "Cód. Proposta",
      align: "center",
      minWidth: "120px",
    },
    {
      title: "Valor Baixado",
      align: "right",
      minWidth: "140px",
    },
    {
      title: "Parcela",
      align: "center",
      minWidth: "80px",
    },
    {
      title: "Titular / Razão social",
      align: "left",
      minWidth: "200px",
    },
    {
      title: "Vidas",
      align: "left",
      minWidth: "120px",
    },
    {
      title: "Vendedor",
      align: "left",
      minWidth: "180px",
    },
    {
      title: "Vencimento",
      align: "center",
      minWidth: "120px",
    },

    {
      title: "% Corretora",
      align: "right",
      minWidth: "120px",
    },
    {
      title: "% Corretor",
      align: "right",
      minWidth: "120px",
    },
    {
      title: "% supervisor",
      align: "right",
      minWidth: "120px",
    },
    {
      title: "% Gerente",
      align: "right",
      minWidth: "120px",
    },
    {
      title: "Ações",
      align: "center",
      minWidth: "80px",
    },
  ];

  return <ThCoreIndex dataTh={dataTh} />;
};

interface ITableAnaliseInstallments {
  step: number;
  setStep: (step: number) => void;
  data: IListUploadInstallments["data"];
}

export const TableAnaliseInstallments = ({ step, setStep, data }: ITableAnaliseInstallments) => {
  const [filterStatus, setFilterStatus] = useState<string>("todas");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [openModalEditar, setOpenModalEditar] = useState(false);
  const [parcelaSelecionada, setParcelaSelecionada] = useState<InstallmentData | null>(null);

  const filteredData = useMemo(() => {
    let filtered = data;

    if (filterStatus !== "todas") {
      filtered = filtered.filter((item) => {
        const statusMap: Record<string, IListUploadInstallments["data"][0]["status"]> = {
          correspondentes: "OK",
          novas: "NEW_INSTALLMENT",
          divergentes: "VALUE_DIVERGENCE",
          nao_encontradas: "CONTRACT_NOT_FOUND",
          recebidas: "IS_RECEIVED",
        };
        return item.status === statusMap[filterStatus];
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.contractCode.toString().includes(term) ||
          item.contratos_titular_pf.toLowerCase().includes(term) ||
          item.contratos_cpf_cnpj.includes(term) ||
          item.nome_produtor.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [filterStatus, searchTerm]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(filteredData.map((item) => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  useEffect(() => {
    handleSelectAll({ target: { checked: true } } as React.ChangeEvent<HTMLInputElement>);
  }, [filteredData]);

  const handleSelectItem = (id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isAllSelected =
    filteredData.length > 0 && selectedItems.length === filteredData.length;
  const isIndeterminate =
    selectedItems.length > 0 && selectedItems.length < filteredData.length;

  const getCountByStatus = (status: string) => {
    if (status === "todas") return data.length;
    const statusMap: Record<string, IListUploadInstallments["data"][0]["status"]> = {
      correspondentes: "OK",
      novas: "NEW_INSTALLMENT",
      divergentes: "VALUE_DIVERGENCE",
      nao_encontradas: "CONTRACT_NOT_FOUND",
      recebidas: "IS_RECEIVED",
    };
    return data.filter((item) => item.status === statusMap[status]).length;
  };

  const handleOpenModalEditar = (item: InstallmentData) => {
    setParcelaSelecionada(item);
    setOpenModalEditar(true);
  };

  const handleSaveEdicao = (valoresEditados: InstallmentData) => {
    data = data.map((item) =>
      item.id === valoresEditados.id ? valoresEditados as unknown as IListUploadInstallments["data"][0] : item
    );
  };

  return (
    <CardCore sxStyle={{ m: 0 }}>
      <ModalEditarParcela
        openModal={openModalEditar}
        setOpenModal={setOpenModalEditar}
        installment={parcelaSelecionada}
        onSave={handleSaveEdicao}
      />
      <Grid container spacing={2} sx={{ alignItems: "center", mb: 2 }}>

        <Grid item xs={12} md={8}>
          <Typography variant="h6" fontWeight="bold" color="text.secondary">
            Parcelas Analisadas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Revise e edite os dados antes de confirmar
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <AppTextField
            label="Pesquisa"
            placeholder="Pesquisar por Código, Nome, CPF/CNPJ"
            fullWidth
            sxStyle={{ opacity: "", pointerEvents: "auto", m: 0 }}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <Tabs
          value={filterStatus}
          onChange={(_, newValue) => setFilterStatus(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: "48px",
            },
          }}
        >
          <Tab
            label={`Todas (${getCountByStatus("todas")})`}
            value="todas"
          />
          <Tab
            label={`Correspondentes (${getCountByStatus("correspondentes")})`}
            value="correspondentes"
          />
          <Tab
            label={`+ Novas (${getCountByStatus("novas")})`}
            value="novas"
          />
          <Tab
            label={`▲ Divergentes (${getCountByStatus("divergentes")})`}
            value="divergentes"
          />
          <Tab
            label={`Não Encontradas (${getCountByStatus("nao_encontradas")})`}
            value="nao_encontradas"
          />
          <Tab
            label={`Recebidas (${getCountByStatus("recebidas")})`}
            value="recebidas"
          />
        </Tabs>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, mt: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={handleSelectAll}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {selectedItems.length} de {filteredData.length} parcelas selecionadas
          </Typography>
        </Box>
        <Box>
          <ButtonCore
            title="Avançar para resumo e confirmação"
            endIcon={<ArrowForwardIcon />}
            onClick={() => setStep(step + 1)}
            disabled={selectedItems.length === 0}
          />
        </Box>
      </Box>

      <TableCore
        THead={<THeadInstallments />}
        qtdList={filteredData.length}
        isNotPagination={true}
        sxStyle={{ margin: 0 }}
        heigthTable={filteredData.length < 10 ? "400px" : filteredData.length < 15 ? "700px" : "1000px"}
      >
        {Children.toArray(
          filteredData.map((item) => (
            <TableRowCore id={item.id}>
              <TdCore
                values={
                  <Checkbox
                    checked={selectedItems.includes(item.id)}
                    onChange={() => handleSelectItem(item.id)}
                    size="small"
                  />
                }
                textAlign="center"
              />
              <TdCore values={getStatusChip(item.status)} />
              <TdCore
                values={
                  <Chip
                    label={item?.contractCode || ""}
                    sx={{ minWidth: "100%", justifyContent: "flex-start", }}
                    color="default"
                  />
                }
                textAlign="center"
              />
              <TdCore
                values={
                  item.valor_contrato ? (
                    <Box>
                      <Box>{formatCurrency(Number(item.fileAmount))}</Box>
                      <Box
                        sx={{
                          fontSize: "0.7rem",
                          color: item.status === "VALUE_DIVERGENCE" ? "#ff9800" : "#3cb916",
                          fontWeight: 500,
                        }}
                      >
                        Sistema: {formatCurrency(Number(item.valor_contrato))}
                      </Box>
                    </Box>
                  ) : (
                    formatCurrency(Number(item.fileAmount))
                  )
                }
                textAlign="right"
              />
              <TdCore values={item.installmentNumber} textAlign="center" />
              <TdCore
                values={item.contratos_titular_pf}
                subTitle={item.contratos_cpf_cnpj} />
              <TdCore values={item.contratos_vidas} />
              <TdCore values={item.nome_produtor} />

              <TdCore
                values={FormatDateBR(item.data_vencimento_parcela)}
                textAlign="center"
              />


              <TdCore
                values={
                  <Box sx={{ color: "#0e6aff" }}>
                    {`${item.porcentagem_comissao_corretora?.replace(".", ",") || "0,00"}%`}
                  </Box>
                }
                textAlign="right"
              />
              <TdCore
                values={
                  <Box sx={{ color: "#0e6aff" }}>
                    {`${item.porcentagem_comissao_corretor?.replace(".", ",") || "0,00"}%`}
                  </Box>
                }
                textAlign="right"
              />
              <TdCore
                values={
                  <Box sx={{ color: "#0e6aff" }}>
                    {`${item.porcentagem_comissao_supervisor?.replace(".", ",") ||
                      "0,00"
                      }%`}
                  </Box>
                }
                textAlign="right"
              />
              <TdCore
                values={
                  <Box sx={{ color: "#0e6aff" }}>
                    {`${item.porcentagem_comissao_gerente?.replace(".", ",") ||
                      "0,00"
                      }%`}
                  </Box>
                }
                textAlign="right"
              />
              <TdCore
                values={
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenModalEditar(item as unknown as InstallmentData)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                }
                textAlign="center"
              />
            </TableRowCore>
          ))
        )}
      </TableCore>

    </CardCore>
  );
};